import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Spend Report Component
 * Shows daily spend breakdown for all access codes with optional filtering
 */
const SpendReport = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialCodeId = queryParams.get('code') || '';

  const [codes, setCodes] = useState([]);
  const [selectedCodeId, setSelectedCodeId] = useState(initialCodeId);
  const [days, setDays] = useState(30);
  const [spendData, setSpendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch available codes on mount
  useEffect(() => {
    fetchCodes();
  }, []);

  // Fetch spend data when filter changes
  useEffect(() => {
    fetchSpendData();
  }, [selectedCodeId, days]);

  const fetchCodes = async () => {
    try {
      const response = await fetch('/api/admin/tools/access-codes', {
        credentials: 'include'
      });
      const data = await response.json();
      setCodes(data.codes || []);
    } catch (err) {
      console.error('Failed to fetch codes:', err);
    }
  };

  const fetchSpendData = async () => {
    setLoading(true);
    setError('');
    try {
      const url = selectedCodeId
        ? `/api/admin/tools/spend-report?days=${days}&code_id=${selectedCodeId}`
        : `/api/admin/tools/spend-report?days=${days}`;

      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setSpendData(data.spendByDay || []);
      }
    } catch (err) {
      setError('Failed to fetch spend data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totals = spendData.reduce((acc, day) => {
    Object.entries(day.byCode).forEach(([codeId, data]) => {
      if (!acc[codeId]) {
        acc[codeId] = { amount: 0, requests: 0, codeName: data.codeName };
      }
      acc[codeId].amount += data.amount;
      acc[codeId].requests += data.requests;
    });
    return acc;
  }, {});

  const grandTotal = Object.values(totals).reduce(
    (acc, t) => ({ amount: acc.amount + t.amount, requests: acc.requests + t.requests }),
    { amount: 0, requests: 0 }
  );

  // Get all unique code IDs from the data
  const codeIdsInData = [...new Set(spendData.flatMap(day => Object.keys(day.byCode)))];

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header>
          <h5 className="mb-0">Spend Report</h5>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Access Code</Form.Label>
                <Form.Select
                  value={selectedCodeId}
                  onChange={(e) => setSelectedCodeId(e.target.value)}
                >
                  <option value="">All Codes</option>
                  {codes.map((code) => (
                    <option key={code.id} value={code.id}>
                      {code.name || code.code} ({code.code})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Time Period</Form.Label>
                <Form.Select
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value))}
                >
                  <option value="7">Last 7 days</option>
                  <option value="14">Last 14 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="60">Last 60 days</option>
                  <option value="90">Last 90 days</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : spendData.length === 0 ? (
            <p className="text-muted">No spend data found for the selected period.</p>
          ) : (
            <>
              {/* Summary */}
              <Card className="mb-4 bg-light">
                <Card.Body>
                  <h6>Summary for {days} days</h6>
                  <Row>
                    <Col md={4}>
                      <strong>Total Spend:</strong> ${grandTotal.amount.toFixed(4)}
                    </Col>
                    <Col md={4}>
                      <strong>Total Requests:</strong> {grandTotal.requests}
                    </Col>
                    <Col md={4}>
                      <strong>Avg/Day:</strong> ${(grandTotal.amount / days).toFixed(4)}
                    </Col>
                  </Row>
                  {Object.keys(totals).length > 1 && (
                    <div className="mt-3">
                      <strong>By Code:</strong>
                      <ul className="mb-0 mt-1">
                        {Object.entries(totals).map(([codeId, data]) => (
                          <li key={codeId}>
                            {data.codeName}: ${data.amount.toFixed(4)} ({data.requests} requests)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Daily breakdown table */}
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Date</th>
                    {selectedCodeId ? (
                      <>
                        <th>Amount</th>
                        <th>Requests</th>
                      </>
                    ) : (
                      <>
                        {codeIdsInData.map(codeId => {
                          const code = codes.find(c => c.id === parseInt(codeId));
                          return (
                            <th key={codeId} colSpan={2} className="text-center">
                              {code?.name || code?.code || `Code ${codeId}`}
                            </th>
                          );
                        })}
                        <th colSpan={2} className="text-center">Total</th>
                      </>
                    )}
                  </tr>
                  {!selectedCodeId && codeIdsInData.length > 0 && (
                    <tr>
                      <th></th>
                      {codeIdsInData.map(codeId => (
                        <React.Fragment key={codeId}>
                          <th>$</th>
                          <th>Req</th>
                        </React.Fragment>
                      ))}
                      <th>$</th>
                      <th>Req</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {spendData.map((day) => {
                    const dayTotal = Object.values(day.byCode).reduce(
                      (acc, d) => ({ amount: acc.amount + d.amount, requests: acc.requests + d.requests }),
                      { amount: 0, requests: 0 }
                    );

                    return (
                      <tr key={day.date}>
                        <td>
                          {new Date(day.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        {selectedCodeId ? (
                          <>
                            <td>${(day.byCode[selectedCodeId]?.amount || 0).toFixed(4)}</td>
                            <td>{day.byCode[selectedCodeId]?.requests || 0}</td>
                          </>
                        ) : (
                          <>
                            {codeIdsInData.map(codeId => (
                              <React.Fragment key={codeId}>
                                <td>${(day.byCode[codeId]?.amount || 0).toFixed(4)}</td>
                                <td>{day.byCode[codeId]?.requests || 0}</td>
                              </React.Fragment>
                            ))}
                            <td><strong>${dayTotal.amount.toFixed(4)}</strong></td>
                            <td><strong>{dayTotal.requests}</strong></td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>
    </AdminToolsLayout>
  );
};

export default SpendReport;
